import { JsonRpcProvider, TransactionReceipt } from 'ethers'
import { useEffect, useMemo, useRef, useState } from 'react'

import { NETWORK_CONFIG, NetworkName } from '@railgun-community/shared-models'
import { getRpcUrlForChainId } from '@web/utils/railgunCore'

export const usePrivateTransactionReceipt = ({
  txid,
  networkName,
  networkChainId
}: {
  txid?: string
  networkName: NetworkName
  networkChainId: bigint
}) => {
  const [receipt, setReceipt] = useState<TransactionReceipt | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const prevTxidRef = useRef<string | undefined>(undefined)

  const historyChainId = useMemo(() => {
    const chainId = NETWORK_CONFIG[networkName]?.chain.id
    return Number(chainId ?? networkChainId ?? 1n)
  }, [networkName, networkChainId])

  useEffect(() => {
    if (!txid) {
      prevTxidRef.current = undefined
      setReceipt(null)
      setIsLoading(false)
      return
    }

    const rpcUrl = getRpcUrlForChainId(historyChainId)
    if (!rpcUrl) {
      prevTxidRef.current = txid
      setReceipt(null)
      setIsLoading(false)
      return
    }

    const provider = new JsonRpcProvider(rpcUrl)
    let cancelled = false

    const txidChanged = prevTxidRef.current !== txid
    prevTxidRef.current = txid
    if (txidChanged) {
      setReceipt(null)
    }
    setIsLoading(true)

    provider
      .getTransactionReceipt(txid)
      .then((nextReceipt) => {
        if (!cancelled) setReceipt(nextReceipt)
      })
      .catch(() => {
        if (!cancelled) setReceipt(null)
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })

    return () => {
      cancelled = true
      provider.destroy()
    }
  }, [historyChainId, txid])

  return {
    receipt,
    isLoading
  }
}
